/**
 * THE BOOTSTRAP GATE — the one moment streaming is allowed to refuse to run.
 *
 * On a fresh world the streaming centre starts at y = 0 while the surface can be hundreds of metres
 * up. Streaming before the centre's height is known generates an entire fill for a place the centre
 * is about to leave, and shows the player inside the ground for a moment. The column TILE carries
 * that height with no chunk resident, so waiting one round trip for it costs nothing — that is what
 * this gate buys, and it is a COLD-START concern only.
 *
 * WHY THIS IS A SEPARATE, TESTED DECISION. The first version of the gate was a plain condition on
 * every pass: "if the centre's column has no height info, return". That is a DEADLOCK, because the
 * code that requests column tiles lives downstream of the gate — so a centre that lands in an
 * untiled column can never become tiled, and streaming stops for good. It survived review and every
 * probe because reaching it needs the centre to move into an untiled column FASTER than a tile round
 * trip: on a machine rendering at 60 fps a fast pan crosses a column every few frames and does it
 * constantly, while the throttled probe container renders at ~1 fps, moves the centre once a second,
 * and always has the tile in hand first. It reproduces as "fast long pan isn't working — chunks
 * don't change after the initial load".
 *
 * So the wait is a LATCH, not a condition: it releases once, on the first column that arrives, and
 * from then on an unknown centre column is ordinary streaming (the request path asks for its tile
 * like any other, which is precisely what the gate was preventing).
 */

export type StreamingGate =
  /** No column has ever been asked for — ask, and run nothing else this pass. */
  | 'request-initial'
  /** Cold start, height still unknown: hold. Reachable only before the first column ever arrives. */
  | 'wait-for-column'
  /** Normal operation. */
  | 'stream';

/**
 * How long the cold-start wait may last before streaming proceeds without the height.
 *
 * A GATE WITH NO ESCAPE HATCH IS A BRICK. This one blocks ALL streaming — fine and coarse — on a
 * single condition, so any environment where that condition never becomes true has no world at all
 * and no way to recover: an empty planet with a working UI. The optimisation it buys (skip one
 * wasted fill on a fresh world) is not worth that risk, and the trade is not close. After the
 * deadline the first fill may be placed against a guessed height, which costs a fill and a visible
 * jump — a bad second, against a dead app.
 *
 * Generous on purpose: a local generate is ~100 ms and a server round trip well under a second, so
 * three seconds only ever elapses when something is actually wrong.
 */
export const BOOTSTRAP_DEADLINE_MS = 3000;

export interface StreamingGateState {
  /** Has the initial surface column been requested yet? */
  initialColumnRequested: boolean;
  /** THE LATCH: has the centre's column height ever been known? Never returns to false. */
  bootstrapped: boolean;
  /** Does the centre's column have height info right now? */
  centreColumnKnown: boolean;
  /** Milliseconds since the initial column was requested (0 when it hasn't been). */
  msSinceRequest: number;
}

/** What streaming may do this pass, plus the latch's new value and whether the deadline fired. */
export function streamingGate(s: StreamingGateState): {
  gate: StreamingGate;
  bootstrapped: boolean;
  timedOut: boolean;
} {
  if (!s.initialColumnRequested) {
    return { gate: 'request-initial', bootstrapped: s.bootstrapped, timedOut: false };
  }
  if (!s.bootstrapped) {
    if (s.centreColumnKnown) return { gate: 'stream', bootstrapped: true, timedOut: false };
    // Give up waiting rather than never stream again.
    if (s.msSinceRequest >= BOOTSTRAP_DEADLINE_MS) {
      return { gate: 'stream', bootstrapped: true, timedOut: true };
    }
    return { gate: 'wait-for-column', bootstrapped: false, timedOut: false };
  }
  return { gate: 'stream', bootstrapped: true, timedOut: false };
}
